/**
 * ActiveSimulationPanel Component
 * Main simulation interface with chat, voice input, and live scoring
 * Voice-only mode for communication skill development
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { AICore } from '../ai/AICore';
import { VoiceWaveform } from '../ai/VoiceWaveform';
import { TTSPlayer } from '../ai/TTSPlayer';
import { SimulationHint } from './SimulationHint';
import { PreCallBrief, CallBriefPanel } from './CallBrief';
import {
    Square,
    Mic,
    MicOff,
    Sparkles,
    AlertCircle,
    Zap,
    FileText,
    Clock,
    TrendingUp,
    Target,
    CheckCircle
} from 'lucide-react';

export const ActiveSimulationPanel = ({
    // Simulation state
    simulation,
    messages,
    scores,
    aiState,
    setAiState,
    
    // Input state
    inputMessage,
    setInputMessage,
    isRecording,
    audioLevel,
    
    // UI state
    realTimeTip,
    setRealTimeTip,
    aiDetectionWarning,
    setAiDetectionWarning,
    responseCountdown,
    ttsFinished,
    autoRecordEnabled,
    setAutoRecordEnabled,
    questionsAsked,
    isAnswering,
    exchangeStatus,
    exchanges,
    
    // Refs
    messagesEndRef,
    autoRecordTimeoutRef,
    
    // Actions
    startRecording,
    stopRecording,
    sendMessage,
    completeSimulation,
    handleTTSComplete,
    setMessages,
    
    // Helpers
    getCurrentScenario,
    getScoreLabels,
    api
}) => {
    const scenario = getCurrentScenario();
    const [briefDone, setBriefDone] = useState(false);
    const hasBrief = Boolean(simulation?.scenario_context?.brief?.facts?.length);
    
    if (hasBrief && !briefDone) {
        return <PreCallBrief simulation={simulation} scenario={scenario} onStart={() => setBriefDone(true)} />;
    }
    
    return (
        <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Simulation Area */}
            <div className="lg:col-span-2 space-y-4">
                {/* AI Character Card */}
                <Card className="overflow-hidden">
                    <div className={`p-6 ${scenario?.bgColor}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                            <div className="flex min-w-0 items-center gap-3">
                                {scenario && (() => {
                                    const ScenarioIcon = scenario.icon;
                                    return (
                                        <div className="p-2 rounded-lg bg-background/50">
                                            <ScenarioIcon className={`h-5 w-5 ${scenario?.color}`} />
                                        </div>
                                    );
                                })()}
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h3 className="font-semibold" data-testid="active-sim-heading">
                                            {simulation?.topic_data?.roleplay ? 'Difficult Candidate Call' : scenario?.title}
                                        </h3>
                                        {/* Difficulty Badge */}
                                        {simulation?.difficulty && (
                                            <Badge 
                                                variant="outline" 
                                                className={`text-xs ${
                                                    simulation.difficulty === 'easy' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' :
                                                    simulation.difficulty === 'medium' ? 'bg-amber-500/10 text-amber-600 border-amber-500/30' :
                                                    'bg-red-500/10 text-red-600 border-red-500/30'
                                                }`}
                                                data-testid="difficulty-badge"
                                            >
                                                {simulation.difficulty.charAt(0).toUpperCase() + simulation.difficulty.slice(1)}
                                                {simulation.difficulty_info?.is_progressive && (
                                                    <TrendingUp className="h-3 w-3 ml-1" />
                                                )}
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        You are the recruiter. Respond naturally and professionally.
                                    </p>
                                    {simulation?.variant_title && (
                                        <p className="mt-1 text-xs font-medium" data-testid="active-variant-title">
                                            Scenario: {simulation.variant_title}
                                            {simulation.scenario_context?.persona && (
                                                <span className="font-normal text-muted-foreground"> · {simulation.scenario_context.persona}</span>
                                            )}
                                        </p>
                                    )}
                                    {!simulation?.variant_title && simulation?.topic_data?.name && (
                                        <p className="mt-1 text-xs font-medium" data-testid="active-topic-title">
                                            Topic: {simulation.topic_data.name}
                                            {simulation.topic_data.roleplay && simulation.topic_data.persona && (
                                                <span className="font-normal text-muted-foreground"> · AI plays {simulation.topic_data.persona}</span>
                                            )}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <Button 
                                variant="destructive" 
                                size="sm" 
                                onClick={completeSimulation}
                                data-testid="end-simulation"
                            >
                                <Square className="h-4 w-4 mr-2" />
                                End Simulation
                            </Button>
                        </div>
                        
                        {/* Progressive Mode Info */}
                        {simulation?.difficulty_info?.is_progressive && (
                            <div className="mb-4 p-2 rounded-lg bg-background/50 text-xs text-center">
                                <span className="text-muted-foreground">
                                    Progressive Mode: {simulation.difficulty_info.completed_count} completed
                                    {simulation.difficulty_info.simulations_until_next > 0 && (
                                        <> • {simulation.difficulty_info.simulations_until_next} more until next level</>
                                    )}
                                </span>
                            </div>
                        )}
                        
                        <div className="flex justify-center py-6">
                            <AICore state={aiState} size="lg" />
                        </div>
                    </div>
                </Card>

                {simulation?.id && (
                    <SimulationHint
                        key={simulation.id}
                        api={api}
                        simulationId={simulation.id}
                        initialUsed={simulation.hints_used || 0}
                        disabled={aiState === 'thinking'}
                    />
                )}

                {/* Conversation */}
                <Card>
                    <CardContent className="p-0">
                        <ScrollArea className="h-[350px] p-4">
                            <div className="space-y-4">
                                {messages.map((msg, idx) => (
                                    <div
                                        key={idx}
                                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div
                                            className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                                                msg.role === 'user'
                                                    ? 'bg-primary text-primary-foreground'
                                                    : 'bg-muted'
                                            }`}
                                        >
                                            {msg.type === 'voice' && (
                                                <span className="text-xs opacity-70 flex items-center gap-1 mb-1">
                                                    <Mic className="h-3 w-3" /> Voice
                                                </span>
                                            )}
                                            <p className="text-sm leading-relaxed">{msg.content}</p>
                                            
                                            {msg.role === 'assistant' && (
                                                <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10">
                                                    <span className="text-xs opacity-50">
                                                        {msg.timestamp && new Date(msg.timestamp).toLocaleTimeString()}
                                                    </span>
                                                    <TTSPlayer 
                                                        text={msg.content}
                                                        api={api}
                                                        autoPlay={msg.autoPlay === true}
                                                        onPlay={() => setAiState('speaking')}
                                                        onEnd={() => {
                                                            setAiState('idle');
                                                            setMessages(prev => prev.map((m, i) => 
                                                                i === idx ? { ...m, autoPlay: false } : m
                                                            ));
                                                            handleTTSComplete();
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </div>
                        </ScrollArea>

                        {/* Voice Waveform */}
                        {isRecording && (
                            <div className="px-4 pb-2">
                                <VoiceWaveform isRecording={isRecording} audioLevel={audioLevel} />
                            </div>
                        )}

                        {/* Real-time Coaching Tip (Communication Simulator) */}
                        {realTimeTip && simulation?.scenario_type === 'communication' && (
                            <div className="px-4 pb-2">
                                <div className="p-3 rounded-lg bg-pink-500/10 border border-pink-500/30 text-sm">
                                    <div className="flex items-center gap-2 text-pink-600 font-medium mb-1">
                                        <Sparkles className="h-4 w-4" />
                                        Coach&apos;s Tip
                                    </div>
                                    <p className="text-muted-foreground">{realTimeTip}</p>
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="mt-2 text-xs"
                                        onClick={() => setRealTimeTip(null)}
                                    >
                                        Got it
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* AI Detection Warning */}
                        {aiDetectionWarning && aiDetectionWarning.length > 0 && (
                            <div className="px-4 pb-2">
                                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm">
                                    <div className="flex items-center gap-2 text-amber-600 font-medium mb-1">
                                        <AlertCircle className="h-4 w-4" />
                                        Fluency Check
                                    </div>
                                    <ul className="text-muted-foreground text-xs space-y-1">
                                        {aiDetectionWarning.map((warning, i) => (
                                            <li key={i}>• {warning}</li>
                                        ))}
                                    </ul>
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="mt-2 text-xs"
                                        onClick={() => setAiDetectionWarning(null)}
                                    >
                                        Dismiss
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Input */}
                        <div className="border-t p-4">
                            {/* Response Countdown Timer */}
                            {responseCountdown !== null && responseCountdown > 0 && !isAnswering && (
                                <div className={`mb-3 p-3 rounded-lg text-center ${responseCountdown <= 5 ? 'bg-red-500/20 border border-red-500/50' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                                    <div className="flex items-center justify-center gap-2">
                                        <Clock className={`h-5 w-5 ${responseCountdown <= 5 ? 'text-red-500 animate-pulse' : 'text-amber-500'}`} />
                                        <span className={`text-lg font-bold ${responseCountdown <= 5 ? 'text-red-500' : 'text-amber-600'}`}>
                                            {responseCountdown}s
                                        </span>
                                    </div>
                                    <p className={`text-xs mt-1 ${responseCountdown <= 5 ? 'text-red-400' : 'text-muted-foreground'}`}>
                                        {responseCountdown <= 5 
                                            ? 'Start speaking now or simulation will end!' 
                                            : 'Start answering within 15 seconds'}
                                    </p>
                                </div>
                            )}
                            
                            {/* Auto-record indicator */}
                            {autoRecordEnabled && ttsFinished && !isRecording && responseCountdown === null && (
                                <div className="mb-3 p-2 rounded bg-primary/10 text-sm text-center animate-pulse">
                                    <Mic className="h-4 w-4 inline mr-2" />
                                    Recording will start automatically...
                                </div>
                            )}
                            {/* Voice-Only Input Area */}
                            <div className="flex flex-col items-center gap-3">
                                <div className="flex items-center gap-4">
                                    {/* Main Mic Button - Large and prominent */}
                                    <Button
                                        variant={isRecording ? 'destructive' : 'default'}
                                        size="lg"
                                        onClick={() => {
                                            if (autoRecordTimeoutRef.current) {
                                                clearTimeout(autoRecordTimeoutRef.current);
                                            }
                                            isRecording ? stopRecording() : startRecording();
                                        }}
                                        data-testid="sim-voice-btn"
                                        className={`h-14 w-14 rounded-full ${isRecording ? 'animate-pulse' : ''}`}
                                    >
                                        {isRecording ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
                                    </Button>
                                </div>
                                
                                {/* Voice-only instruction */}
                                <p className="text-sm text-muted-foreground text-center">
                                    {isRecording 
                                        ? 'Recording... Click mic when done speaking'
                                        : 'Click the mic to speak your response'
                                    }
                                </p>
                                
                                {/* Voice Mode Badge */}
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Mic className="h-3 w-3 text-primary" />
                                    <span>Voice-only mode for communication skill development</span>
                                </div>
                            </div>
                            
                            {/* Auto-record toggle */}
                            <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                    <Zap className="h-3 w-3" />
                                    Auto-record after AI speaks
                                </span>
                                <button
                                    onClick={() => setAutoRecordEnabled(!autoRecordEnabled)}
                                    className={`px-2 py-1 rounded text-xs ${autoRecordEnabled ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
                                    data-testid="auto-record-toggle"
                                >
                                    {autoRecordEnabled ? 'ON' : 'OFF'}
                                </button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Live Scores Sidebar */}
            <div className="space-y-4">
                <CallBriefPanel simulation={simulation} />
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-primary" />
                            Live Performance
                            {simulation?.scenario_type === 'job_analysis' && questionsAsked > 0 && (
                                <Badge variant="outline" className="ml-auto">{questionsAsked} Q&A</Badge>
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="text-center mb-4">
                            <div className="text-4xl font-bold text-primary" data-testid="live-session-score">
                                {scores?.session_average?.overall ?? scores?.overall ?? 0}
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {simulation?.scenario_type === 'job_analysis' ? 'Understanding Score' : 'Overall Score'} (session average)
                            </div>
                            {scores?.session_average && (
                                <div className="mt-1 text-xs text-muted-foreground" data-testid="last-response-score">
                                    Last response: <span className="font-medium text-foreground">{scores.overall}</span>
                                </div>
                            )}
                        </div>
                        
                        <div className="space-y-3">
                            {scores?.session_average && (
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">Last response breakdown</p>
                            )}
                            {getScoreLabels().map((scoreItem, i) => (
                                <div key={i}>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="text-muted-foreground">{scoreItem.label}</span>
                                        <span className="font-medium">{scores?.[scoreItem.key] || 0}</span>
                                    </div>
                                    <Progress value={scores?.[scoreItem.key] || 0} className="h-2" />
                                </div>
                            ))}
                        </div>
                        
                        {scores?.feedback && (
                            <div className="mt-4 p-3 rounded-lg bg-muted/50">
                                <p className="text-xs text-muted-foreground">{scores.feedback}</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Exchange Progress Card */}
                {exchangeStatus && (
                    <Card className={`border-2 ${exchangeStatus.is_near_goal ? 'border-green-500/30 bg-green-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                                <Target className={`h-4 w-4 ${exchangeStatus.is_near_goal ? 'text-green-500' : 'text-amber-500'}`} />
                                Simulation Progress
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {/* Exchange counter */}
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Exchanges</span>
                                    <span className="text-lg font-bold">
                                        {exchangeStatus.exchange_count} / {exchangeStatus.exchange_count + exchangeStatus.remaining_exchanges}
                                    </span>
                                </div>
                                
                                {/* Progress bar */}
                                <Progress 
                                    value={(exchangeStatus.exchange_count / (exchangeStatus.exchange_count + exchangeStatus.remaining_exchanges)) * 100} 
                                    className={`h-2 ${exchangeStatus.is_near_goal ? '[&>div]:bg-green-500' : '[&>div]:bg-amber-500'}`}
                                />
                                
                                {/* Status message */}
                                {exchangeStatus.remaining_exchanges <= 3 && (
                                    <div className={`p-2 rounded text-xs ${exchangeStatus.is_near_goal ? 'bg-green-500/10 text-green-600' : 'bg-amber-500/10 text-amber-600'}`}>
                                        {exchangeStatus.is_near_goal ? (
                                            <span className="flex items-center gap-1">
                                                <CheckCircle className="h-3 w-3" />
                                                On track! {exchangeStatus.remaining_exchanges > 0 ? `${exchangeStatus.remaining_exchanges} exchange${exchangeStatus.remaining_exchanges > 1 ? 's' : ''} left` : 'Completing...'}
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1">
                                                <AlertCircle className="h-3 w-3" />
                                                {exchangeStatus.remaining_exchanges > 0 ? `${exchangeStatus.remaining_exchanges} exchange${exchangeStatus.remaining_exchanges > 1 ? 's' : ''} left - improve your score!` : 'Completing...'}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Job Description Reference - For Job Analysis */}
                {simulation?.scenario_type === 'job_analysis' && simulation?.job_data && (
                    <Card className="border-violet-500/20">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                                <FileText className="h-4 w-4 text-violet-500" />
                                Job Reference
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2 text-xs">
                                <p className="font-medium text-violet-500">{simulation.job_data.job_title}</p>
                                <p className="text-muted-foreground">{simulation.job_data.company_type}</p>
                                <Separator className="my-2" />
                                <details className="cursor-pointer">
                                    <summary className="text-muted-foreground hover:text-foreground">View full job details</summary>
                                    <div className="mt-2 space-y-2 text-muted-foreground">
                                        <p><strong>Summary:</strong> {simulation.job_data.summary}</p>
                                        <p><strong>Experience:</strong> {simulation.job_data.experience}</p>
                                        <p><strong>Skills:</strong> {simulation.job_data.required_skills?.join(', ')}</p>
                                    </div>
                                </details>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Scenario Tips */}
                <Card className="bg-secondary/5 border-secondary/20">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">
                            {simulation?.scenario_type === 'job_analysis' ? 'Analysis Tips' : 'Scenario Tips'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ul className="text-xs text-muted-foreground space-y-2">
                            {(simulation?.variant_id ? simulation.scenario_context?.evaluation_criteria : scenario?.skills)?.map((skill, i) => (
                                <li key={i} className="flex gap-2">
                                    <span className="text-secondary">•</span>
                                    Focus on {skill.toLowerCase()}
                                </li>
                            ))}
                            <li className="flex gap-2">
                                <span className="text-secondary">•</span>
                                Speak naturally and confidently
                            </li>
                        </ul>
                    </CardContent>
                </Card>

                {/* Detailed Feedback Panel */}
                {scores?.detailed_feedback && (
                    <Card className="border-primary/20">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                                <Target className="h-4 w-4 text-primary" />
                                Detailed Feedback
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {getScoreLabels().map((param) => {
                                const feedback = scores.detailed_feedback[param.key];
                                if (!feedback) return null;
                                const ParamIcon = param.icon;
                                
                                return (
                                    <Accordion type="single" collapsible key={param.key} className="border rounded-lg">
                                        <AccordionItem value={param.key} className="border-0">
                                            <AccordionTrigger className="px-3 py-2 hover:no-underline">
                                                <div className="flex items-center gap-3 w-full">
                                                    <div className={`p-1.5 rounded bg-${param.color}-500/10`}>
                                                        <ParamIcon className={`h-4 w-4 text-${param.color}-500`} />
                                                    </div>
                                                    <span className="text-sm font-medium flex-1 text-left">{param.label}</span>
                                                    <Badge variant="outline" className="text-xs">
                                                        {feedback.score || scores[param.key] || 0}/100
                                                    </Badge>
                                                </div>
                                            </AccordionTrigger>
                                            <AccordionContent className="px-3 pb-3">
                                                <div className="space-y-3">
                                                    {feedback.strengths && feedback.strengths.length > 0 && (
                                                        <div>
                                                            <div className="flex items-center gap-1.5 text-emerald-600 text-xs font-medium mb-1">
                                                                <CheckCircle className="h-3 w-3" />
                                                                Strengths
                                                            </div>
                                                            <ul className="space-y-1">
                                                                {feedback.strengths.map((s, i) => (
                                                                    <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                                                        <span className="text-emerald-500 mt-0.5">✓</span>
                                                                        {s}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    {feedback.improvements && feedback.improvements.length > 0 && (
                                                        <div>
                                                            <div className="flex items-center gap-1.5 text-amber-600 text-xs font-medium mb-1">
                                                                <AlertCircle className="h-3 w-3" />
                                                                Areas to Improve
                                                            </div>
                                                            <ul className="space-y-1">
                                                                {feedback.improvements.map((s, i) => (
                                                                    <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                                                        <span className="text-amber-500 mt-0.5">→</span>
                                                                        {s}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            </AccordionContent>
                                        </AccordionItem>
                                    </Accordion>
                                );
                            })}
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
};
